import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { authService } from '../../services/auth.service';
import { useAuthStore } from '../../stores/auth.store';
import { ROUTE_WHITELIST } from '../../lib/deepLinks';

interface LoginForm {
  email: string;
  password: string;
}

/**
 * Anti-open-redirect guard for `state.returnTo` post-login.
 *
 * Accepts only:
 *   - Strings starting with "/" (relative paths).
 *   - Without "://" (blocks `https://evil.com`).
 *   - Without leading "//" (blocks protocol-relative `//evil.com`).
 *   - That match the deep-link {@link ROUTE_WHITELIST}.
 *
 * Anything else → falls back to "/" (home).
 *
 * Reused by deep-link listener → if a deep link aimed at a protected route
 * fires while the user is logged out, the listener stashes the path in
 * `state.returnTo` and `LoginPage` consumes it here.
 */
function safeReturnTo(input: unknown): string {
  if (typeof input !== 'string') return '/';
  if (!input.startsWith('/')) return '/';
  if (input.startsWith('//')) return '/'; // protocol-relative attack
  if (input.includes('://')) return '/'; // absolute URL attack
  // Strip query for whitelist check (returnTo can carry ?focus=:id etc.).
  const pathOnly = input.split('?')[0] ?? '/';
  return (ROUTE_WHITELIST as readonly string[]).includes(pathOnly) ? input : '/';
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isLoading = useAuthStore((state) => state.isLoading);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    setError(null);
    const result = await authService.login(data);

    if (result.success) {
      const returnTo = safeReturnTo(
        (location.state as { returnTo?: unknown } | null)?.returnTo,
      );
      navigate(returnTo);
    } else {
      setError(result.error || 'Error al iniciar sesión');
    }
  };

  return (
    <div>
      {/* Headline */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-extrabold text-gray-800 dark:text-white leading-tight">
          Controla tu dinero en<br />un solo lugar
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-3 text-base">
          Visualiza gastos, ingresos y ahorro<br />sin complicarte.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-xl mb-6 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        {/* Email */}
        <div className="relative">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          </div>
          <input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className={`w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-800 border ${
              errors.email ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-gray-700'
            } rounded-2xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all`}
            placeholder="tu@email.com"
            {...register('email', {
              required: 'El correo es requerido',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Correo invalido',
              },
            })}
          />
          {errors.email && (
            <p className="text-red-500 text-xs mt-1 ml-2">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div className="relative">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
          </div>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className={`w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-800 border ${
              errors.password ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-gray-700'
            } rounded-2xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all`}
            placeholder="••••••••"
            {...register('password', {
              required: 'La contrasena es requerida',
              minLength: {
                value: 8,
                message: 'Minimo 8 caracteres',
              },
            })}
          />
          {errors.password && (
            <p className="text-red-500 text-xs mt-1 ml-2">{errors.password.message}</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          // C33 PR-8 (B.3): el CTA usaba TEAL COMO FONDO. DESIGN-SYSTEM §1.2
          // reserva la acción primaria para la TINTA de marca (`ink-*`) y deja
          // el teal como acento interactivo (links, estados activos): este era
          // el botón más visible de la app contradiciendo esa regla. Y
          // `shadow-lg` no es un token del sistema — el de botones elevados es
          // `shadow-fab`.
          className="w-full py-4 bg-ink-700 hover:bg-ink-600 active:bg-ink-800 dark:bg-ink-600 dark:hover:bg-ink-500 text-white font-semibold text-lg rounded-2xl shadow-fab disabled:opacity-50 disabled:cursor-not-allowed transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Entrando...
            </span>
          ) : (
            'Entrar a mis finanzas'
          )}
        </button>
      </form>

      {/* Links */}
      <div className="text-center mt-6 space-y-3">
        <p className="text-sm">
          <Link
            to="/forgot-password"
            className="text-primary-500 dark:text-primary-400 font-semibold hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
          >
            ¿Olvidaste tu contrasena?
          </Link>
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          ¿No tienes cuenta?{' '}
          <Link
            to="/register"
            className="text-primary-600 dark:text-primary-400 font-semibold hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
          >
            Registrate aqui
          </Link>
        </p>
      </div>
    </div>
  );
}
